/**
 * ErrorBoundary Component
 *
 * Catches React errors in child components and displays fallback UI
 * instead of crashing the entire application.
 *
 * Features:
 * - Terminal theme styling consistent with app design
 * - Error details display for debugging (development only)
 * - Retry functionality to attempt recovery
 * - Production-safe error messaging
 * - WaveSurfer error handling
 * - Convex mutation error handling
 */

import React, { Component, type ReactNode } from 'react'
import { X } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: Error, retry: () => void) => ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)

    // Log additional context for debugging
    if (error.message?.includes('WaveSurfer')) {
      console.error('[ErrorBoundary] Audio player error detected')
    }
    if (error.message?.includes('Convex')) {
      console.error('[ErrorBoundary] Database mutation error detected')
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry)
      }

      // Default fallback UI with terminal theme
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-black border-4 border-red-500 shadow-xl w-full max-w-2xl mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b-2 border-red-500">
              <div className="flex items-center gap-3">
                <X className="w-6 h-6 text-red-500" />
                <h2 className="text-xl font-bold text-red-500 font-mono tracking-wider">
                  [SYSTEM ERROR]
                </h2>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4 p-4 border-2 border-red-500 bg-red-500/10">
                <p className="text-red-500 font-mono text-sm">
                  An unexpected error occurred. The application state has been preserved.
                </p>
                <p className="text-trail-tan font-mono text-xs mt-2">
                  {this.state.error.message}
                </p>
              </div>

              {/* Error Details (development only) */}
              {/* eslint-disable-next-line no-undef */}
              {process.env.NODE_ENV === 'development' && this.state.error.stack && (
                <div className="mb-4 p-4 border-2 border-yellow-500 bg-yellow-500/10 max-h-48 overflow-auto">
                  <div className="text-yellow-500 font-mono text-xs mb-2">Stack Trace:</div>
                  <pre className="text-gray-400 font-mono text-xs whitespace-pre-wrap">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 bg-terminal-green text-black font-bold font-mono py-3 px-6
                           hover:bg-green-400 transition-colors"
                >
                  [RETRY]
                </button>
                <button
                  onClick={() => window.location.href = '/app/account'}
                  className="flex-1 bg-sky-blue text-black font-bold font-mono py-3 px-6
                           hover:bg-blue-400 transition-colors"
                >
                  [DASHBOARD]
                </button>
              </div>

              {/* Instructions */}
              <div className="mt-4 text-trail-tan font-mono text-xs">
                <p className="mb-1">If the error persists:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Try refreshing the page</li>
                  <li>Clear browser cache and reload</li>
                  <li>Contact support if issue continues</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
